import type { TypeVisibilitySettingsWithoutUnresolvableLinksResponse } from '~/types/contentful/TypeVisibilitySettings'
import type {
  VisibilityDecision,
  VisibilityDenialReason,
} from '~/types/visibility'
import { evaluateVisibilityRulesDetailed } from '~/utils/visibilityRules'

interface VisibilityStore {
  groups?: Array<{ id?: string }>
}

interface EvaluateVisibilityDecisionOptions {
  visibility:
    | TypeVisibilitySettingsWithoutUnresolvableLinksResponse
    | undefined
    | null
  loggedIn: boolean
  highestUserLevel: number
  stores: Array<VisibilityStore | null | undefined>
  country: string
  locale: string
  debug?: boolean
  userAccessCheck: (
    level: TypeVisibilitySettingsWithoutUnresolvableLinksResponse['fields']['accessLevel'],
    highestLevel: number,
  ) => boolean
}

export function evaluateVisibilityDecision({
  visibility,
  loggedIn,
  highestUserLevel,
  stores,
  country,
  locale,
  debug = false,
  userAccessCheck,
}: EvaluateVisibilityDecisionOptions): VisibilityDecision {
  const debugLog = (step: string, details: Record<string, unknown>) => {
    if (debug) console.log(`[visibilityDecision] ${step}`, details)
  }

  debugLog('input', {
    visibilityId: visibility?.sys?.id,
    loggedIn,
    highestUserLevel,
    country,
    locale,
  })

  if (!visibility) {
    const decision: VisibilityDecision = { visible: true }
    debugLog('no visibility settings', { decision })
    return decision
  }

  const reasons = new Set<VisibilityDenialReason>()
  const loginGate = visibility.fields?.mustBeLoggedIn

  if (loginGate === 'Yes' || loginGate === 'Yes, and must have a store') {
    if (!loggedIn) {
      reasons.add('not-logged-in')
    } else if (
      loginGate === 'Yes, and must have a store' &&
      !stores.some((store) => store != null)
    ) {
      reasons.add('no-store')
    }
  }

  debugLog('login gate', {
    mustBeLoggedIn: loginGate,
    loggedIn,
    hasStore: stores.some((store) => store != null),
    reasons: [...reasons],
  })

  const userHasAccess = userAccessCheck(
    visibility.fields?.accessLevel,
    highestUserLevel,
  )

  if (!userHasAccess) {
    reasons.add(loggedIn ? 'access-level' : 'not-logged-in')
  }

  debugLog('access level', {
    requiredLevel: visibility.fields?.accessLevel,
    highestUserLevel,
    passed: userHasAccess,
    reasons: [...reasons],
  })

  const requiredGroupIds = visibility.fields?.groupIDs || []
  const userHasGroup =
    requiredGroupIds.length === 0 ||
    stores.some((store) =>
      store?.groups?.some((group) => requiredGroupIds.includes(group.id || '')),
    )

  if (!userHasGroup) {
    reasons.add(loggedIn ? 'group' : 'not-logged-in')
  }

  debugLog('group', {
    requiredGroupIds,
    passed: userHasGroup,
    reasons: [...reasons],
  })

  const rules = evaluateVisibilityRulesDetailed({
    countries:
      visibility.fields?.countries
        ?.map((entry) => entry?.fields?.value)
        .filter((value): value is string => Boolean(value)) || [],
    languages:
      visibility.fields?.languages
        ?.map((entry) => entry?.fields?.value)
        .filter((value): value is string => Boolean(value)) || [],
    countryMode: visibility.fields?.countryInclusionExclusion,
    languageMode: visibility.fields?.languageInclusionExclusion,
    countryCookie: country,
    locale,
    debug,
  })

  if (rules.pass === false) {
    for (const reason of rules.reasons) reasons.add(reason)
  }

  debugLog('country and language', {
    country,
    locale,
    result: rules,
    reasons: [...reasons],
  })

  if (reasons.size > 0) {
    const decision: VisibilityDecision = {
      visible: false,
      reasons: [...reasons],
    }
    debugLog('decision', { decision })
    return decision
  }

  const decision: VisibilityDecision =
    rules.pass === null ? { visible: null } : { visible: true }
  debugLog('decision', { decision })
  return decision
}
